import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AdminFilterBar } from "@/components/admin/AdminFilterBar";
import { AdminDetailPanel } from "@/components/admin/AdminDetailPanel";
import {
  Target, Users, Building2, ChevronRight, Plus, Clock, AlertTriangle,
  CheckCircle, TrendingUp, MapPin, Phone, Mail, Star, Loader2,
  ArrowRight, Calendar, FileText, BarChart3
} from "lucide-react";

const STAGE_COLORS = {
  prospect_identified: "bg-slate-100 text-slate-700",
  initial_contact: "bg-blue-100 text-blue-700",
  qualified: "bg-indigo-100 text-indigo-700",
  meeting_scheduled: "bg-violet-100 text-violet-700",
  presented_demo: "bg-purple-100 text-purple-700",
  verbal_commitment: "bg-amber-100 text-amber-700",
  onboarding_in_progress: "bg-emerald-100 text-emerald-700",
  live_published: "bg-green-100 text-green-700",
  stalled_lost: "bg-red-100 text-red-700",
};
const TIER_COLORS = { A: "bg-green-500", B: "bg-blue-500", C: "bg-amber-500" };
const TASK_COLORS = { completed: "text-green-500", in_progress: "text-blue-500", pending_venue: "text-amber-500", not_started: "text-slate-300", incomplete: "text-red-500" };

export default function RecruitmentDashboard() {
  const [kpis, setKpis] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pipeline");
  const [addOpen, setAddOpen] = useState(false);
  const [detailProspect, setDetailProspect] = useState(null);
  const [moveStage, setMoveStage] = useState("");
  const [moveNote, setMoveNote] = useState("");
  const [moveFollowUp, setMoveFollowUp] = useState("");
  const [newProspect, setNewProspect] = useState({ venue_name: "", venue_type: "Private", municipality: "", address: "", contact_person_name: "", phone: "", tier: "B", lead_source: "field_outreach", sports_category: [], pain_points: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");

  const fetchAll = useCallback(async () => {
    try {
      const [kRes, pRes, cRes] = await Promise.all([
        api.get("/recruitment/kpis"),
        api.get("/recruitment/pipeline"),
        api.get("/recruitment/config"),
      ]);
      setKpis(kRes.data);
      setPipeline(pRes.data);
      setConfig(cRes.data);
    } catch { toast.error("Failed to load recruitment data"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createProspect = async () => {
    if (!newProspect.venue_name.trim()) { toast.error("Venue name required"); return; }
    try {
      await api.post("/recruitment/prospects", newProspect);
      toast.success("Prospect created!");
      setAddOpen(false);
      setNewProspect({ venue_name: "", venue_type: "Private", municipality: "", address: "", contact_person_name: "", phone: "", tier: "B", lead_source: "field_outreach", sports_category: [], pain_points: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openDetail = async (pid) => {
    try {
      const res = await api.get(`/recruitment/prospects/${pid}`);
      setDetailProspect(res.data);
    } catch { toast.error("Failed to load prospect"); }
  };

  const handleMove = async () => {
    if (!detailProspect || !moveStage) return;
    try {
      await api.post(`/recruitment/prospects/${detailProspect.prospect_id}/move`, {
        stage: moveStage, next_follow_up_date: moveFollowUp || null, note: moveNote,
      });
      toast.success("Stage updated!");
      openDetail(detailProspect.prospect_id);
      fetchAll();
      setMoveStage(""); setMoveNote(""); setMoveFollowUp("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const updateTask = async (taskId, status) => {
    if (!detailProspect) return;
    try {
      await api.post(`/recruitment/prospects/${detailProspect.prospect_id}/onboarding`, { task_id: taskId, status });
      openDetail(detailProspect.prospect_id);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const logActivity = async (type, details) => {
    if (!detailProspect) return;
    try {
      await api.post(`/recruitment/prospects/${detailProspect.prospect_id}/activity`, { activity_type: type, details });
      toast.success("Activity logged");
      openDetail(detailProspect.prospect_id);
    } catch { toast.error("Failed"); }
  };

  const supply = kpis?.supply || {};
  const pipeKpis = kpis?.pipeline || {};
  const prospects = pipeline?.prospects || [];
  const stages = config?.stages || [];
  const filteredProspects = useMemo(() => prospects.filter((prospect) => {
    const searchText = [prospect.venue_name, prospect.contact_person_name, prospect.municipality, prospect.phone].filter(Boolean).join(" ").toLowerCase();
    const matchesSearch = !searchQuery || searchText.includes(searchQuery.toLowerCase());
    const matchesStage = stageFilter === "all" || prospect.current_stage === stageFilter;
    const matchesTier = tierFilter === "all" || prospect.tier === tierFilter;
    return matchesSearch && matchesStage && matchesTier;
  }), [prospects, searchQuery, stageFilter, tierFilter]);
  const recruitmentFilterChips = [
    searchQuery ? { key: "search", label: `Search: ${searchQuery}`, onRemove: () => setSearchQuery("") } : null,
    stageFilter !== "all" ? { key: "stage", label: `Stage: ${stageFilter.replace(/_/g, ' ')}`, onRemove: () => setStageFilter("all") } : null,
    tierFilter !== "all" ? { key: "tier", label: `Tier: ${tierFilter}`, onRemove: () => setTierFilter("all") } : null,
  ].filter(Boolean);

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6" data-testid="recruitment-dashboard">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Target className="w-6 h-6 text-primary" />
          <h1 className="font-heading text-2xl sm:text-3xl">Venue Recruitment</h1>
        </div>
        <Button onClick={() => setAddOpen(true)} className="rounded-full" data-testid="add-prospect-btn">
          <Plus className="w-4 h-4 mr-1" />New Prospect
        </Button>
      </div>

      {/* Risk Alerts */}
      {kpis?.alerts?.length > 0 && (
        <div className="space-y-2">
          {kpis.alerts.map((a, i) => (
            <div key={i} className={`p-3 rounded-lg flex items-center gap-2 text-sm ${a.level === "critical" ? "bg-red-50 text-red-800 border border-red-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
              <AlertTriangle className="w-4 h-4 shrink-0" />{a.message}
            </div>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="kpis">KPIs</TabsTrigger>
          <TabsTrigger value="clusters">Clusters</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
        </TabsList>

        {/* Pipeline Kanban */}
        <TabsContent value="pipeline" className="mt-4">
          <AdminFilterBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search prospects by venue, contact, municipality"
            filters={[
              { key: "stage", label: "Stage", value: stageFilter, onChange: setStageFilter, options: [{ value: "all", label: "All Stages" }, ...stages.map((stage) => ({ value: stage.id, label: stage.label }))], testId: "recruit-stage-filter" },
              { key: "tier", label: "Tier", value: tierFilter, onChange: setTierFilter, options: [{ value: "all", label: "All Tiers" }, { value: "A", label: "Tier A" }, { value: "B", label: "Tier B" }, { value: "C", label: "Tier C" }], testId: "recruit-tier-filter" },
            ]}
            activeFilters={recruitmentFilterChips}
            onClearAll={() => { setSearchQuery(""); setStageFilter("all"); setTierFilter("all"); }}
            searchTestId="recruit-search-input"
            extraActions={<Button onClick={() => setAddOpen(true)} className="rounded-md" data-testid="add-prospect-btn"><Plus className="w-4 h-4 mr-1" />New Prospect</Button>}
          />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-6 border border-zinc-200 bg-white">
            <div className="lg:col-span-8 border-r border-zinc-200">
              {filteredProspects.length === 0 ? <div className="p-8 text-center text-zinc-500">No prospects match the current filters.</div> : filteredProspects.map((prospect) => (
                <button key={prospect.prospect_id} type="button" className="w-full px-6 py-4 border-b border-zinc-200 hover:bg-zinc-50 text-left transition-colors" onClick={() => openDetail(prospect.prospect_id)} data-testid={`prospect-${prospect.prospect_id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-zinc-900 truncate">{prospect.venue_name}</p>
                        <Badge className={STAGE_COLORS[prospect.current_stage] || "bg-zinc-100 text-zinc-700"}>{prospect.current_stage?.replace(/_/g, ' ')}</Badge>
                        <Badge className={`${TIER_COLORS[prospect.tier] || 'bg-slate-400'} text-white`}>Tier {prospect.tier}</Badge>
                      </div>
                      <p className="text-xs text-zinc-500">{prospect.contact_person_name || 'No contact'} · {prospect.municipality || 'No municipality'} · {prospect.phone || 'No phone'}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-zinc-500">{prospect.created_at?.slice(0, 10)}</p>
                      {prospect.days_in_stage > 5 && <Badge className="mt-1 bg-amber-100 text-amber-800 text-[9px]">{prospect.days_in_stage}d in stage</Badge>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="lg:col-span-4">
              <AdminDetailPanel title={detailProspect?.venue_name} subtitle={detailProspect ? `${detailProspect.contact_person_name || 'No contact'} · ${detailProspect.municipality || 'No municipality'}` : 'Select a prospect to review details'} testId="recruit-detail-panel">
                {detailProspect ? (
                  <div className="space-y-4">
                    <div className="space-y-2 text-sm text-zinc-700">
                      <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-zinc-400" />{detailProspect.address || detailProspect.municipality || 'No address'}</div>
                      <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-zinc-400" />{detailProspect.phone || '—'}</div>
                      <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-zinc-400" />{detailProspect.contact_person_name || '—'}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 space-y-2">
                      <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Pain Points</p><p className="text-sm">{detailProspect.pain_points || '—'}</p></div>
                      <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">Current Stage</p><p className="text-sm capitalize">{detailProspect.current_stage?.replace(/_/g, ' ')}</p></div>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">Move Stage</p>
                      <div className="space-y-2">
                        <Select value={moveStage} onValueChange={setMoveStage}>
                          <SelectTrigger data-testid="recruit-move-stage"><SelectValue placeholder="Choose next stage" /></SelectTrigger>
                          <SelectContent>{stages.map((stage) => <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="date" value={moveFollowUp} onChange={e => setMoveFollowUp(e.target.value)} data-testid="recruit-follow-up-date" />
                        <Textarea value={moveNote} onChange={e => setMoveNote(e.target.value)} placeholder="Stage note" data-testid="recruit-stage-note" />
                        <Button onClick={handleMove} className="w-full rounded-md" data-testid="recruit-move-stage-btn">Move Prospect</Button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </AdminDetailPanel>
            </div>
          </div>
        </TabsContent>

        {/* KPIs */}
        <TabsContent value="kpis" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card><CardContent className="p-4 text-center">
              <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{supply.venues_live || 0}</p>
              <p className="text-xs text-muted-foreground">Venues Live</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <TrendingUp className="w-5 h-5 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{supply.conversion_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Conversion Rate</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <Building2 className="w-5 h-5 text-purple-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{supply.venues_onboarding || 0}</p>
              <p className="text-xs text-muted-foreground">In Onboarding</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{pipeKpis.stalled_deals || 0}</p>
              <p className="text-xs text-muted-foreground">Stalled Deals</p>
            </CardContent></Card>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Card><CardContent className="p-3 text-center">
              <p className="text-lg font-bold">{supply.weekly_go_live || 0}</p>
              <p className="text-[10px] text-muted-foreground">Go-Live This Week</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-lg font-bold">{pipeKpis.weekly_meetings || 0}</p>
              <p className="text-[10px] text-muted-foreground">Meetings This Week</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-lg font-bold">{pipeKpis.onboarding_backlog || 0}</p>
              <p className="text-[10px] text-muted-foreground">Onboarding Backlog &gt;48h</p>
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* Clusters */}
        <TabsContent value="clusters" className="space-y-3 mt-4">
          {(kpis?.clusters || []).length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No live clusters yet</CardContent></Card>
          ) : (kpis?.clusters || []).map((c, i) => (
            <Card key={i}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium text-sm">{c.municipality}</p>
                    <p className="text-xs text-muted-foreground">{c.venues_live} venues live</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">{Array.from({length: 5}).map((_,j) => (
                    <div key={j} className={`w-2 h-6 rounded-sm ${j < c.density_score ? "bg-primary" : "bg-muted"}`} />
                  ))}</div>
                  {c.event_ready && <Badge className="bg-green-100 text-green-800 text-[9px]">Event Ready</Badge>}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Team Performance */}
        <TabsContent value="team" className="space-y-3 mt-4">
          {(kpis?.coordinators || []).map((c, i) => (
            <Card key={i}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{c.name}</p>
                  <p className="text-xs text-muted-foreground">{c.total} prospects · {c.live} live · {c.verbal} verbal</p>
                </div>
                <div className="text-right">
                  <p className="font-heading font-bold text-primary">{c.conversion}%</p>
                  <p className="text-[10px] text-muted-foreground">conversion</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* ── Add Prospect Dialog ── */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">New Venue Prospect</DialogTitle>
            <DialogDescription>Add a new venue to the recruitment pipeline</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Venue Name *</Label><Input value={newProspect.venue_name} onChange={e => setNewProspect({...newProspect, venue_name: e.target.value})} data-testid="prospect-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Type</Label>
                <Select value={newProspect.venue_type} onValueChange={v => setNewProspect({...newProspect, venue_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Private","HOA","Barangay","LGU","Corporate"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Tier</Label>
                <Select value={newProspect.tier} onValueChange={v => setNewProspect({...newProspect, tier: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["A","B","C"].map(t => <SelectItem key={t} value={t}>Tier {t}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs">Municipality</Label><Input value={newProspect.municipality} onChange={e => setNewProspect({...newProspect, municipality: e.target.value})} /></div>
            <div><Label className="text-xs">Contact Person</Label><Input value={newProspect.contact_person_name} onChange={e => setNewProspect({...newProspect, contact_person_name: e.target.value})} /></div>
            <div><Label className="text-xs">Phone</Label><Input value={newProspect.phone} onChange={e => setNewProspect({...newProspect, phone: e.target.value})} /></div>
            <div><Label className="text-xs">Pain Points</Label><Textarea value={newProspect.pain_points} onChange={e => setNewProspect({...newProspect, pain_points: e.target.value})} className="text-sm" /></div>
            <Button onClick={createProspect} className="w-full rounded-full" data-testid="create-prospect-btn">Create Prospect</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Prospect Detail Dialog ── */}
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {detailProspect?.venue_name}
              <Badge className={STAGE_COLORS[detailProspect?.current_stage] || "bg-muted"}>{detailProspect?.current_stage?.replace(/_/g, " ")}</Badge>
            </DialogTitle>
          </DialogHeader>
          {detailProspect && (
            <div className="space-y-4">
              {/* Info */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Type:</span> {detailProspect.venue_type}</div>
                <div><span className="text-muted-foreground">Tier:</span> <Badge className={`${TIER_COLORS[detailProspect.tier]} text-white text-xs`}>{detailProspect.tier}</Badge></div>
                <div><span className="text-muted-foreground">Municipality:</span> {detailProspect.municipality || "—"}</div>
                <div><span className="text-muted-foreground">Contact:</span> {detailProspect.contact_person_name || "—"}</div>
                {detailProspect.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{detailProspect.phone}</div>}
                {detailProspect.pain_points && <div className="col-span-2 text-xs"><span className="text-muted-foreground">Pain Points:</span> {detailProspect.pain_points}</div>}
              </div>

              <Separator />

              {/* Move Stage */}
              <div>
                <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-2">Move Pipeline Stage</p>
                <div className="flex gap-2 flex-wrap">
                  <Select value={moveStage} onValueChange={setMoveStage}>
                    <SelectTrigger className="w-48"><SelectValue placeholder="Select stage" /></SelectTrigger>
                    <SelectContent>{(config?.stages || []).map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="date" value={moveFollowUp} onChange={e => setMoveFollowUp(e.target.value)} className="w-40" placeholder="Follow-up" />
                  <Button size="sm" onClick={handleMove} disabled={!moveStage} data-testid="move-stage-btn">
                    <ArrowRight className="w-3 h-3 mr-1" />Move
                  </Button>
                </div>
              </div>

              {/* Onboarding Checklist */}
              {detailProspect.current_stage === "onboarding_in_progress" && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Onboarding ({detailProspect.onboarding_progress}%)</p>
                    <span className="text-xs font-bold text-primary">{detailProspect.onboarding_progress}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden mb-3">
                    <div className="h-full bg-primary rounded-full transition-all" style={{width: `${detailProspect.onboarding_progress}%`}} />
                  </div>
                  <div className="space-y-2">
                    {(detailProspect.onboarding_checklist || []).map(task => {
                      const status = detailProspect.onboarding_tasks?.[task.task_id] || "not_started";
                      return (
                        <div key={task.task_id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                          <div className="flex items-center gap-2">
                            <CheckCircle className={`w-4 h-4 ${TASK_COLORS[status]}`} />
                            <span className="text-sm">{task.label}</span>
                            {task.required && <Badge variant="outline" className="text-[8px]">Req</Badge>}
                          </div>
                          <Select value={status} onValueChange={v => updateTask(task.task_id, v)}>
                            <SelectTrigger className="w-32 h-7 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="not_started">Not Started</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="pending_venue">Pending Venue</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Activity Log */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Activity Log</p>
                  <Button size="sm" variant="outline" onClick={() => {
                    const note = prompt("Add a note:");
                    if (note) logActivity("note", note);
                  }}><FileText className="w-3 h-3 mr-1" />Add Note</Button>
                </div>
                {(detailProspect.activities || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-3">No activities yet</p>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {detailProspect.activities.map(a => (
                      <div key={a.activity_id} className="text-xs border-l-2 border-primary/30 pl-2 py-1">
                        <div className="flex justify-between">
                          <span className="font-medium capitalize">{a.activity_type.replace(/_/g, " ")}</span>
                          <span className="text-muted-foreground">{a.timestamp?.slice(0, 10)}</span>
                        </div>
                        <p className="text-muted-foreground">{a.details}</p>
                        <p className="text-[10px] text-muted-foreground">by {a.coordinator_name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
